/**
 * KeepSpatial - AR Integration Script
 *
 * Usage:
 * <script src="https://app.keep.lynkware.com/spatial.js"
 *         data-api-key="your-api-key"
 *         defer></script>
 *
 * <keep-spatial sku="PRODUCT-SKU"></keep-spatial>
 */

import { initConfig, getApiKey, getApiBase, VERSION } from './config.js';
import { fetchModel, recordHit } from './api.js';
import { onDomReady, observeNewElements, scanExistingElements } from './utils/dom.js';
import { isIOS, isAndroid, supportsARQuickLook, supportsSceneViewer, getBestARMode } from './utils/device.js';
import { getDefaultStyles, getARIcon, getSpinnerIcon } from './styles.js';

// Store config before DOM manipulation can change currentScript
initConfig();

/**
 * KeepSpatial Web Component
 * Renders AR button and handles AR launch
 * Uses Shadow DOM for style isolation
 */
class KeepSpatialElement extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.modelData = null;
    this.loading = false;
    this.initialized = false;
    this._clickHandler = null;
  }

  static get observedAttributes() {
    return ['sku', 'api-key', 'button-text', 'button-class', 'show-3d-fallback'];
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.renderLoading();
    this.loadModel();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    this._clickHandler = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.initialized) {
      if (name === 'sku') {
        this.loadModel();
      } else if (name === 'button-text' && this.modelData?.available) {
        this.renderButton();
      }
    }
  }

  // Attribute getters
  get sku() {
    return this.getAttribute('sku') || '';
  }

  get apiKey() {
    return this.getAttribute('api-key') || getApiKey();
  }

  get buttonText() {
    return this.getAttribute('button-text') || 'View in AR';
  }

  get buttonClass() {
    return this.getAttribute('button-class') || '';
  }

  get show3dFallback() {
    return this.getAttribute('show-3d-fallback') === 'true';
  }

  /**
   * Render loading state
   */
  renderLoading() {
    this.shadowRoot.innerHTML = `
      <style>${getDefaultStyles()}</style>
      <div class="keep-spatial-loading" part="loading">
        ${getSpinnerIcon()}
      </div>
    `;
  }

  /**
   * Render empty (hide component gracefully)
   */
  renderEmpty() {
    this.shadowRoot.innerHTML = '';
    this.setAttribute('hidden', '');
  }

  /**
   * Render the AR button
   */
  renderButton() {
    this.removeAttribute('hidden');

    this.shadowRoot.innerHTML = `
      <style>${getDefaultStyles()}</style>
      <div class="keep-spatial-container" part="container">
        <button class="keep-spatial-button ${this.buttonClass}" part="button">
          <span class="icon" part="icon">${getARIcon()}</span>
          <span class="text" part="text">${this.buttonText}</span>
        </button>
      </div>
    `;

    const button = this.shadowRoot.querySelector('button');
    this._clickHandler = () => this.launchAR();
    button.addEventListener('click', this._clickHandler);
  }

  /**
   * Render model-viewer for 3D fallback
   */
  async renderModelViewer() {
    const { glbUrl, usdzUrl, posterUrl, name } = this.modelData;

    this.removeAttribute('hidden');

    // Show loading while we load model-viewer library
    this.renderLoading();

    // Load model-viewer library if not already loaded
    await this.loadModelViewerLibrary();

    // Note: No scale attribute needed - GLB/USDZ are pre-scaled to real-world size
    // ar-scale="fixed" prevents users from resizing in AR mode

    this.shadowRoot.innerHTML = `
      <style>${getDefaultStyles()}</style>
      <div class="keep-spatial-viewer" part="viewer">
        <model-viewer
          src="${glbUrl}"
          ios-src="${usdzUrl || ''}"
          poster="${posterUrl || ''}"
          alt="${name || 'AR Model'}"
          ar
          ar-modes="webxr scene-viewer quick-look"
          ar-scale="fixed"
          camera-controls
          auto-rotate
          shadow-intensity="1"
          tone-mapping="neutral"
          exposure="0.78"
        >
          <button slot="ar-button" class="keep-spatial-button ar-button" part="button">
            <span class="icon" part="icon">${getARIcon()}</span>
            <span class="text" part="text">${this.buttonText}</span>
          </button>
        </model-viewer>
      </div>
    `;
  }

  /**
   * Dynamically load model-viewer library from CDN
   */
  async loadModelViewerLibrary() {
    // Already loaded?
    if (customElements.get('model-viewer')) {
      return;
    }

    // Check if script is already being loaded
    if (window._modelViewerLoading) {
      return window._modelViewerLoading;
    }

    // Load from CDN
    window._modelViewerLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';
      script.onload = () => {
        console.log('[KeepSpatial] model-viewer library loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('[KeepSpatial] Failed to load model-viewer library');
        reject(new Error('Failed to load model-viewer'));
      };
      document.head.appendChild(script);
    });

    return window._modelViewerLoading;
  }

  /**
   * Load model data from API
   */
  async loadModel() {
    if (!this.sku) {
      this.showError('No SKU specified');
      return;
    }

    if (this.loading) return;
    this.loading = true;

    try {
      this.modelData = await fetchModel(this.sku, this.apiKey);

      if (this.modelData && this.modelData.available) {
        const arMode = getBestARMode();
        if (arMode === 'none' && this.modelData.qrCodeUrl) {
          // Desktop: show QR code for scanning
          this.renderQRCode();
        } else if (arMode === 'none' && this.show3dFallback && this.modelData.glbUrl) {
          // Desktop: show 3D viewer fallback
          this.renderModelViewer();
        } else {
          // Mobile/tablet: show AR button
          this.renderButton();
        }
      } else {
        // Graceful hide per FR36
        this.renderEmpty();
      }
    } catch (error) {
      console.error('[KeepSpatial] Failed to load model:', error);
      this.renderEmpty();
    } finally {
      this.loading = false;
    }
  }

  /**
   * Show error and hide component
   * @param {string} message
   */
  showError(message) {
    console.error('[KeepSpatial]', message);
    this.renderEmpty();
  }

  /**
   * Launch AR experience based on device capabilities
   */
  async launchAR() {
    if (!this.modelData) return;

    // Record the hit (fire and forget)
    recordHit(this.sku, this.apiKey);

    const arMode = getBestARMode();

    switch (arMode) {
      case 'quick-look':
        this.launchQuickLook();
        break;
      case 'scene-viewer':
        this.launchSceneViewer();
        break;
      default:
        this.launchFallback();
    }
  }

  /**
   * Launch iOS AR Quick Look
   */
  launchQuickLook() {
    const { usdzUrl, posterUrl } = this.modelData;

    if (!usdzUrl) {
      this.launchFallback();
      return;
    }

    const anchor = document.createElement('a');
    anchor.rel = 'ar';
    anchor.href = usdzUrl + '#allowsContentScaling=0';

    // Add poster image for preview (required by Quick Look)
    if (posterUrl) {
      const img = document.createElement('img');
      img.src = posterUrl;
      img.style.display = 'none';
      anchor.appendChild(img);
    }

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  /**
   * Launch Android Scene Viewer
   */
  launchSceneViewer() {
    const { glbUrl, name } = this.modelData;

    if (!glbUrl) {
      this.launchFallback();
      return;
    }

    const params = new URLSearchParams({
      file: glbUrl,
      mode: 'ar_preferred',
      title: name || 'View in AR',
      resizable: 'false',
    });

    const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?${params.toString()}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(window.location.href)};end;`;

    window.location.href = intentUrl;
  }

  /**
   * Render QR code for desktop users
   */
  renderQRCode() {
    const { qrCodeUrl, name } = this.modelData;

    this.removeAttribute('hidden');

    this.shadowRoot.innerHTML = `
      <style>${getDefaultStyles()}</style>
      <div class="keep-spatial-qr" part="qr">
        <img src="${qrCodeUrl}" alt="QR Code - ${name || this.sku}" class="keep-spatial-qr-img" part="qr-img" />
        <p class="keep-spatial-qr-text" part="qr-text">
          Scannez ce QR code pour voir ce produit en r\u00e9alit\u00e9 augment\u00e9e
        </p>
      </div>
    `;
  }

  /**
   * Fallback for non-AR devices
   */
  launchFallback() {
    const { glbUrl, qrCodeUrl } = this.modelData;

    if (qrCodeUrl) {
      this.renderQRCode();
    } else if (glbUrl && this.show3dFallback) {
      this.renderModelViewer();
    }
  }
}

/**
 * Initialize KeepSpatial
 */
function init() {
  try {
    // Register Web Component if not already registered
    if (!customElements.get('keep-spatial')) {
      customElements.define('keep-spatial', KeepSpatialElement);
    }

    // Scan for existing elements (they auto-initialize via connectedCallback)
    const existingElements = scanExistingElements();
    console.log(`[KeepSpatial] Found ${existingElements.length} element(s)`);

    // Observe for dynamically added elements
    observeNewElements((element) => {
      console.log('[KeepSpatial] New element detected:', element);
    });

    console.log(`[KeepSpatial] Initialized v${VERSION}`);
  } catch (error) {
    console.error('[KeepSpatial] Initialization failed:', error);
  }
}

// Initialize when DOM is ready
onDomReady(init);

// Expose minimal API globally
window.KeepSpatial = {
  version: VERSION,
  getApiKey,
  getApiBase,
  fetchModel,
  recordHit,
  // Device detection
  isIOS,
  isAndroid,
  supportsARQuickLook,
  supportsSceneViewer,
  getBestARMode,
};

export { VERSION, fetchModel, recordHit };
